import React, { useState } from "react";
import { PasswordInput } from "@/components/ui/password-input";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, School as SchoolIcon, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export default function SchoolLogin() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/auth/school-login", { email: form.email, password: form.password });
      setUser(r.data);
      toast.success(`Olá, ${r.data.name}!`);
      nav("/escola", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "E-mail ou senha inválidos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl p-8 card-shadow">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#1e3a8a] flex items-center justify-center mb-3">
              <SchoolIcon className="w-8 h-8 text-white" strokeWidth={2.2} />
            </div>
            <h1 className="font-heading text-2xl font-black text-[#1e3a8a]">Portal da Escola</h1>
            <p className="text-sm text-slate-500 mt-1">Entre para acessar seus orçamentos</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="school-login-email"
                  required
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="voce@escola.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute z-10 pointer-events-none left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <PasswordInput
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="school-login-password"
                  required
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="Sua senha"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="w-full h-11 bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl font-semibold"
              data-testid="school-login-submit"
            >
              {saving ? "Entrando..." : "Entrar"}
            </Button>
            <div className="text-center">
              <Link
                to="/escola/esqueci-senha"
                data-testid="school-login-forgot-link"
                className="text-xs text-[#7c3aed] hover:text-[#6d28d9] hover:underline font-semibold"
              >
                Esqueci minha senha
              </Link>
            </div>
          </form>

          <p className="text-xs text-center text-slate-500 mt-6">
            Problemas com o acesso? Entre em contato com a AMC Escolas para receber um novo link.
          </p>
        </div>

        <div className="mt-4 flex flex-col items-center gap-2">
          <Link
            to="/"
            data-testid="school-login-landing-link"
            className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#7c3aed] hover:text-[#1e3a8a] transition-colors"
          >
            ← Conheça a AMC Escolas
          </Link>
          <Link
            to="/login"
            data-testid="school-login-admin-link"
            className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-[#1e3a8a] transition-colors"
          >
            <Shield className="w-3 h-3" />
            Acessar como administrador
          </Link>
        </div>
      </div>
    </div>
  );
}
